/**
 * Heuristiken für die Anzeige: Zitat-/Thread-Verlauf aus HTML oder Klartext trennen.
 * Nicht für Persistenz oder Versand verwenden — nur Lesen-Ansicht.
 */

export type QuotedParts = {
  main: string;
  quote: string | null;
};

function minPositive(indices: number[]): number | null {
  const valid = indices.filter((n) => n >= 0);
  if (!valid.length) return null;
  return Math.min(...valid);
}

function findHtmlQuoteCutIndex(html: string): number | null {
  const lower = html.toLowerCase();
  const candidates: number[] = [];

  const pushCut = (markerIdx: number | null) => {
    if (markerIdx == null || markerIdx < 0) return;
    candidates.push(cutBeforeNearbyOpeningDiv(html, markerIdx));
  };

  // Structural wrappers (may appear nested later — still collect, then take earliest).
  for (const re of [
    /<div[^>]+\bgmail_quote\b/i,
    /<blockquote\b[^>]*\btype=["']?cite["']?/i,
    /<div[^>]+id=["']divRplyFwdMsg["']/i,
    /<div[^>]+class=["'][^"']*rplyfwdmsg[^"']*["']/i,
    /class=["'][^"']*moz-cite-prefix[^"']*["']/i,
  ]) {
    const idx = html.search(re);
    if (idx >= 0) candidates.push(idx);
  }

  // Outlook / forward markers, with flexible dash counts (--- or -----).
  const markerRes = [
    /-{2,}\s*original message\s*-{2,}/i,
    /-{2,}\s*ursprüngliche nachricht\s*-{2,}/i,
    /begin forwarded message/i,
    /beginnen der weitergeleiteten nachricht/i,
  ];
  for (const re of markerRes) {
    const m = lower.match(re);
    if (m?.index != null) pushCut(m.index);
  }

  // Heading without dashes (legacy portal format).
  for (const mk of ["ursprüngliche nachricht", "original message"]) {
    const idx = lower.indexOf(mk);
    if (idx >= 0) pushCut(idx);
  }

  // Gmail-style cite line.
  const citeMatch = lower.match(/\bam .{8,120} schrieb .+?:/) || lower.match(/\bon .{8,120} wrote:/);
  if (citeMatch?.index != null && citeMatch.index > 0) {
    pushCut(citeMatch.index);
  }

  return minPositive(candidates);
}

/**
 * Prefer cutting at a quote-wrapper <div> (margin-top / border-top) when nearby,
 * otherwise the nearest opening <div>, so we don't leave the separator in "main".
 */
function cutBeforeNearbyOpeningDiv(html: string, markerIdx: number): number {
  const windowStart = Math.max(0, markerIdx - 500);
  const before = html.slice(windowStart, markerIdx);
  const beforeLower = before.toLowerCase();

  // Prefer our quote outer wrappers.
  const wrapperPatterns = ["margin-top:1.25em", "border-top:1px solid", "margin-top:18px"];
  let bestWrapper = -1;
  for (const pattern of wrapperPatterns) {
    let from = 0;
    while (from < beforeLower.length) {
      const found = beforeLower.indexOf(pattern, from);
      if (found < 0) break;
      const divBefore = beforeLower.lastIndexOf("<div", found);
      if (divBefore >= 0) bestWrapper = Math.max(bestWrapper, divBefore);
      from = found + pattern.length;
    }
  }
  if (bestWrapper >= 0) return windowStart + bestWrapper;

  const lastDiv = beforeLower.lastIndexOf("<div");
  if (lastDiv >= 0) return windowStart + lastDiv;
  return markerIdx;
}

function findPlainQuoteCutIndex(lines: string[]): number | null {
  for (let i = 0; i < lines.length; i++) {
    const trimmed = lines[i].trim();

    if (/^On .+ wrote:\s*$/i.test(trimmed)) return i;
    if (/^Am .+ schrieb .+:\s*$/i.test(trimmed)) return i;
    if (/^Am .+, .+ schrieb .+:\s*$/i.test(trimmed)) return i;
    if (/^-{2,}\s*Original Message\s*-{2,}/i.test(trimmed)) return i;
    if (/^-{2,}\s*Ursprüngliche Nachricht\s*-{2,}/i.test(trimmed)) return i;
    if (/^Original Message\s*$/i.test(trimmed)) return i;
    if (/^Ursprüngliche Nachricht\s*$/i.test(trimmed)) return i;
    if (/^_{5,}\s*$/.test(trimmed) && i > 0) return i;
    if (/^-{5,}\s*$/.test(trimmed) && i > 0) return i;
  }

  for (let i = 0; i < lines.length - 1; i++) {
    const a = lines[i].trim();
    const b = lines[i + 1]?.trim() ?? "";
    if (/^Von:\s/i.test(a) && /^Gesendet:\s/i.test(b)) return i;
  }

  if (lines.length > 3) {
    for (let i = 2; i < lines.length; i++) {
      if (lines[i - 1].trim() === "" && /^>+\s?/.test(lines[i])) {
        return i - 1;
      }
    }
  }

  return null;
}

/** Teilt HTML in neuen Inhalt und zitierten Verlauf (falls erkannt). */
export function splitQuotedHtml(html: string): QuotedParts {
  if (!html || typeof html !== "string") return { main: "", quote: null };
  const cut = findHtmlQuoteCutIndex(html);
  if (cut == null || cut <= 0) {
    const main = html.trim();
    return { main, quote: null };
  }
  const main = html.slice(0, cut).trim();
  const quote = html.slice(cut).trim();
  if (!quote) return { main, quote: null };
  return { main: main || "", quote };
}

/** Teilt Klartext in neuen Inhalt und zitierten Verlauf (falls erkannt). */
export function splitQuotedPlainText(text: string): QuotedParts {
  if (!text || typeof text !== "string") return { main: "", quote: null };
  const lines = text.split(/\r?\n/);
  const cut = findPlainQuoteCutIndex(lines);
  if (cut == null || cut <= 0) {
    return { main: text.trim(), quote: null };
  }
  const main = lines.slice(0, cut).join("\n").trim();
  const quote = lines.slice(cut).join("\n").trim();
  if (!quote) return { main, quote: null };
  return { main, quote };
}

/** Entfernt typische Antwort-Wrapper und alles danach (Gmail, Outlook, Marker). */
export function stripQuotedHtml(html: string): string {
  return splitQuotedHtml(html).main;
}

/** Schneidet ab der ersten erkannten Zitat-Trennzeile (mehrsprachig). */
export function stripQuotedPlainText(text: string): string {
  return splitQuotedPlainText(text).main;
}
